import { unclassifiedNuCents } from "./periods";
import type { FinancialSnapshot, TemporaryContribution, WeekId } from "./types";

export type MoneyMapKind = "week" | "savings" | "fund" | "card" | "carryover" | "unclassified";

export interface MoneyMapItem {
  id: string;
  kind: MoneyMapKind;
  label: string;
  amountCents: number;
  percent: number;
  detail: string;
  actionLabel: string;
  targetId?: string;
  weekId?: WeekId;
  paletteIndex: number;
  xPercent: number;
  yPercent: number;
  diameterPercent: number;
}

export interface MoneyMapSource {
  id: string;
  kind: MoneyMapKind;
  label: string;
  amountCents: number;
  detail: string;
  actionLabel: string;
  targetId?: string;
  weekId?: WeekId;
}

interface PackedCircle {
  xPercent: number;
  yPercent: number;
  radiusPercent: number;
}

export interface MoneyMapPosition {
  id: string;
  xPercent: number;
  yPercent: number;
  diameterPercent: number;
}

const MAP_CENTER = 50;
const MAP_RADIUS = 47;
const MAP_PADDING = 0.8;
const PREFERRED_CENTERS: Array<[number, number]> = [
  [34, 33], [70, 31], [68, 64], [33, 65], [50, 82], [18, 76],
  [82, 76], [17, 49], [83, 48], [50, 17], [50, 50], [31, 49],
];

function positiveCents(value: number | undefined): number {
  return Math.max(0, Math.round(value ?? 0));
}

function compactCardName(name: string): string {
  if (name === "AMEX Servicios") return "Amex Servicios";
  if (/American Express Platinum/i.test(name)) return "Platinum Credit Card";
  return name.replace(/ Card$/i, "");
}

function describedPercent(amountCents: number, totalCents: number): string {
  const percent = totalCents > 0 ? amountCents / totalCents * 100 : 0;
  return `${percent.toFixed(1)}% de Nu`;
}

function fundSources(snapshot: FinancialSnapshot, funds: TemporaryContribution[]): MoneyMapSource[] {
  const totalCents = positiveCents(snapshot.temporaryContributionCents);
  if (!totalCents) return [];

  const visible = funds
    .filter((fund) => fund.active !== false && positiveCents(fund.balanceCents) > 0)
    .sort((a, b) => a.order - b.order || a.description.localeCompare(b.description, "es-MX"));
  const visibleTotal = visible.reduce((sum, fund) => sum + positiveCents(fund.balanceCents), 0);

  // Una colección momentáneamente adelantada al snapshot no debe hacer que el mapa
  // clasifique más dinero del que confirma Nu. Se muestra el total hasta sincronizar.
  if (visibleTotal > totalCents) {
    return [{
      id: "fund:syncing-total",
      kind: "fund",
      label: "Fondos por sincronizar",
      amountCents: totalCents,
      detail: `${describedPercent(totalCents, positiveCents(snapshot.accounts[0]?.balanceCents))} · esperando el desglose confirmado`,
      actionLabel: "Ver fondos",
    }];
  }

  const sources = visible.map<MoneyMapSource>((fund) => ({
    id: `fund:${fund.id}`,
    kind: "fund",
    label: fund.description,
    amountCents: positiveCents(fund.balanceCents),
    detail: `${describedPercent(positiveCents(fund.balanceCents), positiveCents(snapshot.accounts[0]?.balanceCents))} · fondo disponible`,
    actionLabel: "Ver este fondo",
    targetId: fund.id,
  }));
  const remainderCents = totalCents - visibleTotal;
  if (remainderCents > 0) {
    sources.push({
      id: "fund:legacy-remainder",
      kind: "fund",
      label: visible.length ? "Otro fondo heredado" : "Aporte temporal migrado",
      amountCents: remainderCents,
      detail: `${describedPercent(remainderCents, positiveCents(snapshot.accounts[0]?.balanceCents))} · saldo heredado todavía sin desglose`,
      actionLabel: "Ver fondos",
    });
  }
  return sources;
}

export function moneyMapSources(snapshot: FinancialSnapshot, funds: TemporaryContribution[]): MoneyMapSource[] {
  const nuCents = positiveCents(snapshot.accounts[0]?.balanceCents);
  if (!nuCents) return [];

  const sources: MoneyMapSource[] = [];
  snapshot.weeks.forEach((week) => {
    const amountCents = positiveCents(week.availableCents);
    if (!amountCents) return;
    const state = week.status === "active" ? "disponible ahora" : week.status === "upcoming" ? "semana siguiente" : "remanente de semana cerrada";
    sources.push({
      id: `week:${week.id}`,
      kind: "week",
      label: week.label,
      amountCents,
      detail: `${describedPercent(amountCents, nuCents)} · ${state}`,
      actionLabel: "Ver semana",
      weekId: week.id,
    });
  });

  const savingsCents = positiveCents(snapshot.savingsCents);
  if (savingsCents) {
    sources.push({
      id: "savings",
      kind: "savings",
      label: "Ahorro",
      amountCents: savingsCents,
      detail: `${describedPercent(savingsCents, nuCents)} · ahorro acumulado`,
      actionLabel: "Ver ahorro",
    });
  }

  sources.push(...fundSources(snapshot, funds));

  snapshot.cardPayments.forEach((card) => {
    const amountCents = positiveCents(card.amountCents);
    if (!amountCents) return;
    sources.push({
      id: `card:${card.name}`,
      kind: "card",
      label: compactCardName(card.name),
      amountCents,
      detail: `${describedPercent(amountCents, nuCents)} · reserva de tarjeta`,
      actionLabel: "Ver tarjeta",
      targetId: card.name,
    });
  });

  const carryoverCents = positiveCents(snapshot.previousPeriodCarryover?.availableCents);
  if (carryoverCents) {
    sources.push({
      id: `carryover:${snapshot.previousPeriodCarryover?.id ?? "previous"}`,
      kind: "carryover",
      label: "Sobrante anterior",
      amountCents: carryoverCents,
      detail: `${describedPercent(carryoverCents, nuCents)} · pendiente de transferir`,
      actionLabel: "Ver semana activa",
      weekId: snapshot.weeks.find((week) => week.status === "active")?.id,
    });
  }

  const unclassifiedCents = unclassifiedNuCents(snapshot);
  if (unclassifiedCents) {
    sources.push({
      id: "unclassified",
      kind: "unclassified",
      label: "Sin clasificar",
      amountCents: unclassifiedCents,
      detail: `${describedPercent(unclassifiedCents, nuCents)} · todavía sin destino visible`,
      actionLabel: "Comprender este saldo",
    });
  }

  return sources.sort((a, b) => b.amountCents - a.amountCents || a.id.localeCompare(b.id));
}

function fitsInsideMap(xPercent: number, yPercent: number, radiusPercent: number): boolean {
  return Math.hypot(xPercent - MAP_CENTER, yPercent - MAP_CENTER) + radiusPercent <= MAP_RADIUS;
}

export function constrainMoneyMapPosition(xPercent: number, yPercent: number, diameterPercent: number): { xPercent: number; yPercent: number } {
  const radiusPercent = Math.max(0, diameterPercent / 2);
  const availableRadius = Math.max(0, MAP_RADIUS - radiusPercent);
  const dx = xPercent - MAP_CENTER;
  const dy = yPercent - MAP_CENTER;
  const distance = Math.hypot(dx, dy);
  if (!distance || distance <= availableRadius) return { xPercent, yPercent };
  const scale = availableRadius / distance;
  return {
    xPercent: MAP_CENTER + dx * scale,
    yPercent: MAP_CENTER + dy * scale,
  };
}

export function moneyMapPositionsAreValid(positions: MoneyMapPosition[]): boolean {
  return positions.every((position, index) => {
    const radiusPercent = Math.max(0, position.diameterPercent / 2);
    if (!fitsInsideMap(position.xPercent, position.yPercent, radiusPercent)) return false;
    return positions.slice(index + 1).every((other) => {
      const distance = Math.hypot(position.xPercent - other.xPercent, position.yPercent - other.yPercent);
      return distance >= radiusPercent + Math.max(0, other.diameterPercent / 2) + MAP_PADDING;
    });
  });
}

function overlapsPacked(xPercent: number, yPercent: number, radiusPercent: number, packed: PackedCircle[]): boolean {
  return packed.some((circle) => Math.hypot(xPercent - circle.xPercent, yPercent - circle.yPercent) < radiusPercent + circle.radiusPercent + MAP_PADDING);
}

function radiusFor(amountCents: number, totalCents: number, scale: number): number {
  const share = totalCents > 0 ? amountCents / totalCents : 0;
  const proportional = Math.min(23.5, Math.sqrt(share) * 37);
  return Math.max(4.6, Math.max(6.7, proportional) * scale);
}

function bestPosition(index: number, radiusPercent: number, packed: PackedCircle[]): PackedCircle | null {
  const preferred = PREFERRED_CENTERS[index % PREFERRED_CENTERS.length];
  let best: PackedCircle | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let yPercent = 3; yPercent <= 97; yPercent += 1) {
    for (let xPercent = 3; xPercent <= 97; xPercent += 1) {
      if (!fitsInsideMap(xPercent, yPercent, radiusPercent) || overlapsPacked(xPercent, yPercent, radiusPercent, packed)) continue;
      const preferredDistance = Math.hypot(xPercent - preferred[0], yPercent - preferred[1]);
      const centerDistance = Math.hypot(xPercent - MAP_CENTER, yPercent - MAP_CENTER);
      const score = preferredDistance + centerDistance * 0.025;
      if (score < bestScore) {
        best = { xPercent, yPercent, radiusPercent };
        bestScore = score;
      }
    }
  }
  return best;
}

function packSources(sources: MoneyMapSource[], totalCents: number): PackedCircle[] {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    const scale = 1 - attempt * 0.02;
    const packed: PackedCircle[] = [];
    let failed = false;
    sources.forEach((source, index) => {
      if (failed) return;
      const position = bestPosition(index, radiusFor(source.amountCents, totalCents, scale), packed);
      if (!position) { failed = true; return; }
      packed.push(position);
    });
    if (!failed) return packed;
  }
  throw new Error("No fue posible acomodar las clasificaciones dentro del mapa vivo.");
}

export function moneyMapLayout(snapshot: FinancialSnapshot, funds: TemporaryContribution[]): MoneyMapItem[] {
  const nuCents = positiveCents(snapshot.accounts[0]?.balanceCents);
  const sources = moneyMapSources(snapshot, funds);
  if (!sources.length || !nuCents) return [];
  const positions = packSources(sources, nuCents);
  const paletteCounts = new Map<MoneyMapKind, number>();

  return sources.map((source, index) => {
    const paletteIndex = paletteCounts.get(source.kind) ?? 0;
    paletteCounts.set(source.kind, paletteIndex + 1);
    return {
      ...source,
      percent: source.amountCents / nuCents * 100,
      paletteIndex,
      xPercent: positions[index].xPercent,
      yPercent: positions[index].yPercent,
      diameterPercent: positions[index].radiusPercent * 2,
    };
  });
}
